import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProduction, listVoices } from '../api';
import { useClientId } from '../hooks/useClient';
import type { Voice } from '../types/db';
import {
  scheduleUIToCron,
  getTimeOptions,
  frequencyLabel,
  type FrequencyOption,
} from '../utils/scheduleUtils';

const PLACEHOLDER_MAP: Record<string, string> = {
  TERRITORIO: 'Bergamo',
  ORA: 'sette',
  GIORNO_SETTIMANA: 'domenica',
  DATA: 'primo giugno',
};

function renderPreview(template: string, brandName: string): string {
  return template
    .replace(/{TERRITORIO}/g, PLACEHOLDER_MAP.TERRITORIO)
    .replace(/{ORA}/g, PLACEHOLDER_MAP.ORA)
    .replace(/{GIORNO_SETTIMANA}/g, PLACEHOLDER_MAP.GIORNO_SETTIMANA)
    .replace(/{DATA}/g, PLACEHOLDER_MAP.DATA)
    .replace(/{BRAND_NAME}/g, brandName || 'VoiceKit');
}

function defaultApiBase(baseUrl: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/wp-json/wp/v2`;
}

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function NewProduction() {
  const navigate = useNavigate();
  const clientId = useClientId();
  const [step, setStep] = useState(1);

  const [voices, setVoices] = useState<Voice[]>([]);

  // Step 1 — Fonte WP + Identità
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiBase, setApiBase] = useState('');
  const [brandName, setBrandName] = useState('');
  const [tagline, setTagline] = useState('');
  const [opening, setOpening] = useState(
    'Buongiorno dalla redazione di {TERRITORIO}, sono le {ORA} di {GIORNO_SETTIMANA} {DATA}, ecco le notizie di {BRAND_NAME}.',
  );
  const [closing, setClosing] = useState('Grazie da {BRAND_NAME}. Buona giornata.');

  // Step 2 — Voce + output + schedule
  const [voiceId, setVoiceId] = useState('');
  const [outputAudio, setOutputAudio] = useState(true);
  const [outputVodcast, setOutputVodcast] = useState(false);
  const [freq, setFreq] = useState<FrequencyOption>('manual');
  const [time, setTime] = useState('07:00');
  const [customCron, setCustomCron] = useState('');
  const [timezone, setTimezone] = useState('Europe/Rome');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listVoices(clientId)
      .then(setVoices)
      .catch(() => setVoices([]));
  }, [clientId]);

  // Auto-suggest slug e api_base se vuoti
  useEffect(() => {
    if (!slug && name) setSlug(autoSlug(name));
  }, [name, slug]);
  useEffect(() => {
    if (baseUrl && !apiBase) setApiBase(defaultApiBase(baseUrl));
  }, [baseUrl, apiBase]);

  const openingPreview = useMemo(() => renderPreview(opening, brandName), [opening, brandName]);
  const closingPreview = useMemo(() => renderPreview(closing, brandName), [closing, brandName]);

  const canAdvance1 =
    name.trim().length > 0 &&
    slug.trim().length > 0 &&
    baseUrl.trim().startsWith('http') &&
    apiBase.trim().startsWith('http') &&
    brandName.trim().length > 0;

  const scheduleSummary = useMemo(() => {
    if (freq === 'manual') return 'Manuale';
    if (freq === 'custom') return customCron.trim() || '—';
    return `${frequencyLabel(freq)} alle ${time}`;
  }, [freq, time, customCron]);

  const handleCreate = async () => {
    setError('');
    setSubmitting(true);
    try {
      const cron = scheduleUIToCron({ frequency: freq, time, customCron });
      const p = await createProduction({
        client_id: clientId,
        name: name.trim(),
        slug: slug.trim(),
        base_url: baseUrl.trim().replace(/\/+$/, ''),
        api_base: apiBase.trim().replace(/\/+$/, ''),
        active: true,
        brand_name: brandName.trim(),
        tagline: tagline.trim() || null,
        opening_template: opening,
        closing_template: closing,
        default_voice_id: voiceId || null,
        output_audio: outputAudio,
        output_vodcast: outputVodcast,
        schedule_cron: cron,
        schedule_timezone: timezone,
        status: 'active',
      });
      navigate(`/dashboard/production/${p.id}`);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Errore creazione';
      // Sprint Pricing v0: trigger enforce_max_productions throw con prefisso noto
      const friendly = raw.includes('Cap testate del piano raggiunto') ? raw : raw;
      setError(friendly);
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                s === step
                  ? 'bg-brand-600 text-white'
                  : s < step
                    ? 'bg-brand-200 text-brand-800'
                    : 'bg-slate-200 text-slate-500'
              }`}
            >
              {s < step ? '✓' : s}
            </div>
            <span className="text-xs font-medium text-slate-500">
              {s === 1 ? 'Fonte + Brand' : s === 2 ? 'Tecnico' : 'Riepilogo'}
            </span>
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      {step === 1 && (
        <div className="space-y-5">
          {/* Fonte WP */}
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Fonte WordPress</h2>

            <div>
              <label className="block text-sm font-medium text-slate-700">Nome testata</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="es. Prima Bergamo"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="es. prima-bergamo"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">URL del sito WP</label>
              <input
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://primabergamo.it"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Endpoint REST API</label>
              <input
                type="url"
                value={apiBase}
                onChange={(e) => setApiBase(e.target.value)}
                placeholder="https://primabergamo.it/wp-json/wp/v2"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Compilato automaticamente come <code>{'{URL}/wp-json/wp/v2'}</code>, modificabile.
              </p>
            </div>
          </div>

          {/* Identità editoriale */}
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Identità editoriale</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700">Brand pronunciato</label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="es. Ops News"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Esattamente come va letto dalla voce TTS (es. "Ops News" non "OPS NEWS").
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Tagline (opzionale)
              </label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="es. Il tuo aggiornamento quotidiano"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Template */}
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Template</h2>
            <p className="text-xs text-slate-500">
              Placeholder:{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-slate-700">
                {'{TERRITORIO} {ORA} {GIORNO_SETTIMANA} {DATA} {BRAND_NAME}'}
              </code>
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700">Apertura</label>
              <textarea
                value={opening}
                onChange={(e) => setOpening(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Chiusura</label>
              <textarea
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Anteprima */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Anteprima</h2>
            <div className="mt-3 space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm italic text-slate-700">
                {openingPreview}
              </div>
              <div className="rounded-lg bg-slate-50 p-3 text-sm italic text-slate-700">
                {closingPreview}
              </div>
            </div>
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!canAdvance1}
            className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            Avanti
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Voce</h2>
            <select
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Voce libreria (default)</option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Output</h2>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={outputAudio}
                onChange={(e) => setOutputAudio(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-slate-700">Genera audio (MP3)</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={outputVodcast}
                onChange={(e) => setOutputVodcast(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-slate-700">Genera vodcast (MP4)</span>
            </label>
          </div>

          <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-700">Schedule</h2>

            <div>
              <label className="block text-sm font-medium text-slate-700">Frequenza</label>
              <select
                value={freq}
                onChange={(e) => setFreq(e.target.value as FrequencyOption)}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {(['manual', 'daily', 'weekdays', 'weekends', 'custom'] as FrequencyOption[]).map(
                  (f) => (
                    <option key={f} value={f}>
                      {frequencyLabel(f)}
                    </option>
                  ),
                )}
              </select>
            </div>

            {freq !== 'manual' && freq !== 'custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Orario</label>
                <select
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  {getTimeOptions().map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {freq === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Cron avanzato</label>
                <input
                  type="text"
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="0 7 * * *"
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="Europe/Rome">Europe/Rome</option>
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Europe/London">Europe/London</option>
              </select>
            </div>

            <p className="text-xs text-slate-500">
              Lo scheduler automatico verrà attivato in uno sprint dedicato. Per ora le edizioni
              vanno avviate manualmente dalla tab Output.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Indietro
            </button>
            <button
              onClick={() => setStep(3)}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              Avanti
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-700">
            <h2 className="text-sm font-semibold text-slate-700">Riepilogo</h2>
            <div className="grid grid-cols-2 gap-2">
              <span className="text-slate-500">Nome</span>
              <span className="font-medium">{name}</span>
              <span className="text-slate-500">Slug</span>
              <span className="font-medium">{slug}</span>
              <span className="text-slate-500">URL WP</span>
              <span className="truncate font-medium">{baseUrl}</span>
              <span className="text-slate-500">API base</span>
              <span className="truncate font-medium">{apiBase}</span>
              <span className="text-slate-500">Brand</span>
              <span className="font-medium">{brandName}</span>
              <span className="text-slate-500">Tagline</span>
              <span className="font-medium">{tagline || '—'}</span>
              <span className="text-slate-500">Voce</span>
              <span className="font-medium">
                {voices.find((v) => v.id === voiceId)?.name || 'Default libreria'}
              </span>
              <span className="text-slate-500">Output</span>
              <span className="font-medium">
                {outputAudio && 'MP3 '}
                {outputVodcast && 'MP4 '}
              </span>
              <span className="text-slate-500">Schedule</span>
              <span className="font-medium">{scheduleSummary}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep(2)}
              className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Indietro
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Creazione…' : 'Crea testata'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
